import styled from '@emotion/styled';
import { Icon } from 'semantic-ui-react';
import { screenSizes } from 'src/assets/styles/Literals';

const OptionSelectGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${(props) => props.columns || 2}, minmax(0, 1fr));
  gap: 0.875rem;
  align-items: stretch;

  @media only screen and (max-width: ${screenSizes.tablet}) {
    grid-template-columns: ${(props) =>
      props.columns === 1 || props.stackOnTablet
        ? '1fr'
        : `repeat(${props.columns || 2}, minmax(0, 1fr))`};
    gap: 0.75rem;
  }

  @media only screen and (max-width: ${screenSizes.mobile}) {
    gap: 0.5rem;
  }
`;

const OptionSelectCard = styled.button`
  display: flex;
  align-items: ${(props) => (props.rich ? 'flex-start' : 'center')};
  gap: ${(props) => (props.rich ? '1rem' : '0.75rem')};
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  padding: ${(props) => (props.rich ? '1.1rem 1.25rem' : '1rem 1.1rem')};
  border: 1.5px solid
    ${(props) => {
      if (props.error) return '#e0b4b4';
      if (props.selected) return '#4db8e8';
      return '#e2e8f0';
    }};
  border-radius: 14px;
  background: ${(props) => {
    if (props.error) return '#fff6f6';
    if (props.selected) return '#e8f6fc';
    return '#fff';
  }};
  box-shadow: ${(props) => (props.selected ? '0 0 0 1px #4db8e8' : 'none')};
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;

  @media only screen and (max-width: ${screenSizes.mobile}) {
    padding: ${(props) => (props.rich ? '0.75rem 0.85rem' : '0.7rem 0.75rem')};
    gap: ${(props) => (props.rich ? '0.65rem' : '0.5rem')};
    border-radius: 10px;
  }

  @media only screen and (min-width: calc(${screenSizes.tablet} + 1px)) {
    ${(props) =>
      props.stacked
        ? `
      flex-direction: column;
      align-items: flex-start;
      gap: 0.85rem;
      padding: 1.35rem 1.4rem;
    `
        : ''}
  }

  &:hover {
    border-color: ${(props) => (props.selected ? '#4db8e8' : '#cbd5e1')};
    background: ${(props) => (props.selected ? '#e8f6fc' : '#fafafa')};
  }

  .radio-dot {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 2px solid ${(props) => (props.selected ? '#4db8e8' : '#cbd5e1')};
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: border-color 0.15s ease;

    &::after {
      content: '';
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: #4db8e8;
      opacity: ${(props) => (props.selected ? 1 : 0)};
      transition: opacity 0.15s ease;
    }
  }

  .option-label {
    font-weight: 600;
    font-size: 0.95rem;
    color: #1a1a1a;

    @media only screen and (max-width: ${screenSizes.mobile}) {
      font-size: 0.85rem;
    }
  }

  .card-icon {
    color: ${(props) => (props.selected ? '#4db8e8' : '#1a1a1a')};
    font-size: 1.35rem;
    margin-top: 0.1rem;
    flex-shrink: 0;
    transition: color 0.15s ease;

    @media only screen and (max-width: ${screenSizes.mobile}) {
      font-size: 1.15rem;
      margin-top: 0;
    }

    @media only screen and (min-width: calc(${screenSizes.tablet} + 1px)) {
      margin-top: 0;
      font-size: 1.6rem;
    }
  }

  .card-content {
    flex: 1;
    min-width: 0;
  }

  .card-title {
    font-weight: 700;
    font-size: 0.95rem;
    color: #1a1a1a;
    margin: 0 0 0.25rem;
    line-height: 1.35;

    @media only screen and (max-width: ${screenSizes.mobile}) {
      font-size: 0.88rem;
      margin-bottom: 0.15rem;
    }

    @media only screen and (min-width: calc(${screenSizes.tablet} + 1px)) {
      font-size: 1rem;
      margin-bottom: 0.35rem;
    }
  }

  .card-subtitle {
    font-size: 0.85rem;
    color: #666;
    margin: 0;
    line-height: 1.45;

    @media only screen and (max-width: ${screenSizes.mobile}) {
      font-size: 0.78rem;
      line-height: 1.35;
    }
  }

  .pref-check {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: 2px solid ${(props) => (props.selected ? '#4db8e8' : '#d1d9e0')};
    background: ${(props) => (props.selected ? '#4db8e8' : '#fff')};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-top: 0.1rem;
    margin-left: auto;
    transition: border-color 0.15s ease, background 0.15s ease;

    i.icon {
      color: #fff;
      margin: 0 !important;
      font-size: 0.85rem;
    }
  }

  ${(props) =>
    props.stacked
      ? `
    position: relative;

    @media only screen and (min-width: calc(${screenSizes.tablet} + 1px)) {
      .pref-check {
        position: absolute;
        top: 1.1rem;
        right: 1.1rem;
        margin: 0;
      }

      .card-icon {
        margin-top: 0;
        font-size: 1.6rem;
      }

      .card-title {
        font-size: 1rem;
        margin-bottom: 0.35rem;
      }
    }
  `
      : ''}
`;

const OptionSelectList = ({
  options = [],
  value,
  onChange,
  error = false,
  columns = 2,
  stackOnTablet = false,
  indicator = 'radio',
}) => (
  <OptionSelectGrid columns={columns} stackOnTablet={stackOnTablet}>
    {options.map((option) => {
      const selected = value === option.value;
      const isRich = Boolean(option.title || option.subtitle || option.icon);

      return (
        <OptionSelectCard
          key={option.value}
          type="button"
          selected={selected}
          error={error}
          rich={isRich}
          stacked={isRich && columns > 1}
          onClick={() => onChange(option.value)}
        >
          {indicator === 'radio' && <span className="radio-dot" aria-hidden />}
          {option.icon && <Icon name={option.icon} className="card-icon" />}
          {isRich ? (
            <div className="card-content">
              <p className="card-title">{option.title}</p>
              {option.subtitle && <p className="card-subtitle">{option.subtitle}</p>}
            </div>
          ) : (
            <span className="option-label">{option.label}</span>
          )}
          {indicator === 'check' && (
            <span className="pref-check" aria-hidden>
              {selected && <Icon name="check" />}
            </span>
          )}
        </OptionSelectCard>
      );
    })}
  </OptionSelectGrid>
);

export default OptionSelectList;
